// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { useState, useRef } from 'react';

// Custom Scripts
import { ErrorState, StateError } from '../errorHandling';


// ----------------------------------------------------------------------------
//   State Enum
// ----------------------------------------------------------------------------

export const WebcamState = Object.freeze({
  NONE:    "NONE",
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
  SKIPPED: "SKIPPED",
});


// ----------------------------------------------------------------------------
//   Custom React Hook(s)
// ----------------------------------------------------------------------------

export function useWebcamObject({ ref = null, initialState = null }) {

  // Use refs for synchronous lock and stream tracking (prevents race conditions)
  const isStartingRef    = useRef( false );
  const activeStreamsRef = useRef( new Set() );

  // Call React useState hook
  const [ webcamState, setWebcamState ] = useState({
    state:   initialState ?? WebcamState.NONE,
    ref,
    error:   null,
    devices: { audio: null, video: null },
    blurred: false,
    flipped: true,
  });

  // Return a manager object that exposes current state & transform methods
  return {

    // Getters
    getState:       () => webcamState.state,
    getError:       () => webcamState.error,
    getRef:         () => webcamState.ref,
    getAudioDevice: () => webcamState.devices.audio,
    getVideoDevice: () => webcamState.devices.video,
    isBlurred:      () => webcamState.blurred,
    isFlipped:      () => webcamState.flipped,

    // Retrieve the full data object
    getAllData:     () => webcamState,

    // Check state value
    stateIs: ( ...targetStates ) => {
      return targetStates.includes( webcamState.state )
    },


    // ------------------------------------------------------------------------
    //   Change State
    // ------------------------------------------------------------------------

    // Reset to NONE state
    turnOff: () => {
      setWebcamState((currentState) => ({ ...currentState, state: WebcamState.NONE, devices: { audio: null, video: null }, error: null }));
    },

    // Mark the webcam as loading
    setLoading: () => {
      setWebcamState((currentState) => ({ ...currentState, state: WebcamState.LOADING }));
    },

    // Update the state to store the loaded value
    setActive: (value) => {
      setWebcamState((currentState) => ({ ...currentState, state: WebcamState.SUCCESS, error: null }));
    },

    // Update the state to track the error
    setFailure: (error) => {
      const errorObject = new StateError( ErrorState.NO_WEBCAM, error );
      setWebcamState((currentState) => ({ ...currentState, state: WebcamState.FAILURE, error: errorObject }));
    },


    // ------------------------------------------------------------------------
    //   Cosmetic Effects
    // ------------------------------------------------------------------------

    toggleBlurred: ( blurred ) => {
      if (blurred === undefined) blurred = !webcamState.blurred;
      setWebcamState((currentState) => ({ ...currentState, blurred }));
    },

    toggleFlipped: ( flipped ) => {
      if (flipped === undefined) flipped = !webcamState.flipped;
      setWebcamState((currentState) => ({ ...currentState, flipped }));
    },


    // ------------------------------------------------------------------------
    //   Update Devices
    // ------------------------------------------------------------------------

    /**
     * Explicitly set (or update) the device ID for the audio and/or video source(s),
     * and re-request a media stream using the specified device(s).
     */
    updateDevices: async ({ video = null, audio = null }) => {
      await _startWebcam({
        webcamState, setWebcamState, isStartingRef, activeStreamsRef,
        videoDeviceID: video ?? webcamState.devices.video,
        audioDeviceID: audio ?? webcamState.devices.audio,
      });
    },

    /**
     * Explicitly set (or update) the device ID for the audio source,
     * and re-request a media stream using the specified device(s).
     */
    setAudioDevice: async ( audio ) => {
      await _startWebcam({
        webcamState, setWebcamState, isStartingRef, activeStreamsRef,
        videoDeviceID:          webcamState.devices.video,
        audioDeviceID: audio ?? webcamState.devices.audio,
      });
    },

    /**
     * Explicitly set (or update) the device ID for the video source,
     * and re-request a media stream using the specified device(s).
     */
    setVideoDevice: async ( video ) => {
      await _startWebcam({
        webcamState, setWebcamState, isStartingRef, activeStreamsRef,
        videoDeviceID: video ?? webcamState.devices.video,
        audioDeviceID:          webcamState.devices.audio,
      });
    },

  };
}


// ----------------------------------------------------------------------------
//   Helper Function(s)
// ----------------------------------------------------------------------------

/**
 * Request a new media stream, optionally specifying the audio and video device IDs to use.
 * Stores the source device IDs in the `audioDevice` and `videoDevice` state variables.
 */
async function _startWebcam({
  webcamState,
  setWebcamState,
  isStartingRef,
  activeStreamsRef,
  videoDeviceID = null,
  audioDeviceID = null,
}) {

  // Check lock - abort if already starting a stream (synchronous check)
  if (isStartingRef.current) {
    console.debug('Webcam stream creation already in progress, ignoring call');
    return;
  }

  // Set lock (synchronous, prevents race condition)
  isStartingRef.current = true;

  // Internal update function
  const _updateDevices = (devices) => {
    setWebcamState((currentState) => {
      return { ...currentState, devices: { ...currentState.devices, ...devices } }
    })
  }

  try {

    // Get the child elements by reference
    const $webcam = webcamState.ref.current;

    // Check if current stream already uses the requested devices
    if ($webcam.srcObject) {
      const currentTracks = $webcam.srcObject.getTracks();
      const currentAudioTrack = currentTracks.find(t => t.kind === 'audio');
      const currentVideoTrack = currentTracks.find(t => t.kind === 'video');

      const currentAudioDevice = currentAudioTrack?.getSettings().deviceId;
      const currentVideoDevice = currentVideoTrack?.getSettings().deviceId;

      // If both devices match (or no specific device requested), no need to swap
      const audioMatches = !audioDeviceID || currentAudioDevice === audioDeviceID;
      const videoMatches = !videoDeviceID || currentVideoDevice === videoDeviceID;

      // Already using the correct devices
      if (audioMatches && videoMatches) return;
    }

    // If explicit device IDs given, use those, otherwise fallback to basic requirements
    const constraints = {
      audio: audioDeviceID ? { deviceId: { exact: audioDeviceID } } : {
        sampleRate: 44000,
        echoCancellation: false,
        noiseSuppression: false,
      },
      video: videoDeviceID ? { deviceId: { exact: videoDeviceID } } : {
        facingMode: "user",
      },
    }

    // Attach camera to webcam stream video element
    const webcamStream = await navigator.mediaDevices.getUserMedia(constraints);

    // Clean up all previously tracked streams
    activeStreamsRef.current.forEach(stream => {
      stream.getTracks().forEach(track => track.stop());
    });

    // Track the new stream and clear old references
    activeStreamsRef.current.clear();
    activeStreamsRef.current.add(webcamStream);

    // Assign new stream to video element
    $webcam.srcObject = webcamStream;

    // Extract the devices used to make this stream and update internal state
    // NOTE: This implicitly assumes there will only be one track of each type (audio and video)
    webcamStream.getTracks().forEach((track) => {
      if (track.kind === "audio") {
        _updateDevices({ audio: track.getSettings().deviceId });
      }
      else if (track.kind === "video") {
        _updateDevices({ video: track.getSettings().deviceId });
      }
    })

    // Start playing the video stream on the <video> element
    try {
      await $webcam.play();
    }

    // Ignore abort errors caused by multiple play calls
    catch (e) {
      console.log("Caught: ", e)
      if (e.name !== 'AbortError') {
        throw e;
      }
    }

  }

  // Always release lock (synchronous)
  finally {
    isStartingRef.current = false;
  }
}
